<template>
  <v-flex v-if="fleet" class="fleet-page">
    <v-layout
      v-if="fleetState && fleetState.infosPanelVisible"
      :style="{ flexGrow: 0, flexShrink: 0, position: 'relative' }"
      class="ma-2 my-3 pa-2 py-3 fleet-infos fleet-resume elevation-2"
    >
      <v-flex class="inline-block xs4 sm2 text-xs-center" style="min-width: min-content">
        <strong>{{ $t('Pilot.FleetInfos.FleetStatus') }}</strong>
        <br class="hidden-sm-and-down" />
        <span class="data">{{ $t(`GliderWaitingMode.${waitingMode}`) }}</span>
      </v-flex>
      <v-flex class="inline-block xs4 sm2 text-xs-center" style="min-width: min-content">
        <strong>{{ $t('Pilot.FleetInfos.StartDate') }}</strong>
        <br class="hidden-sm-and-down" />
        <span class="data">{{ startDate | formatDate('DD/MM/YYYY') }}</span>
      </v-flex>
      <v-flex class="inline-block xs4 sm2 text-xs-center" style="min-width: min-content">
        <strong>{{ $t('Pilot.FleetInfos.EndDate') }}</strong>
        <br class="hidden-sm-and-down" />
        <span class="data">
          {{
            fleet.fleActifOn ? $t('Pilot.MissionInfos.OnGoing') : $options.filters.formatDate(lastDate, 'DD/MM/YYYY')
          }}
        </span>
      </v-flex>
      <v-flex class="inline-block xs4 sm2 text-xs-center" style="min-width: min-content">
        <strong>{{ $t('Pilot.FleetInfos.MissionDuration') }}</strong>
        <br class="hidden-sm-and-down" />
        <span class="data">{{ [startDate, lastDate] | formatDuree($i18n) }}</span>
      </v-flex>
      <v-flex class="inline-block xs4 sm2 text-xs-center" style="min-width: min-content">
        <strong>{{ $t('Pilot.FleetInfos.LastConnect') }}</strong>
        <br class="hidden-sm-and-down" />
        <span class="data">{{ lastDate | formatDate }}</span>
      </v-flex>
      <v-flex class="inline-block xs4 sm2 text-xs-center" style="min-width: min-content">
        <strong>{{ $t('Pilot.FleetInfos.NbGlider') }}</strong>
        <br class="hidden-sm-and-down" />
        <span class="data">{{ fleet.activeMissionIds.length }}</span>
      </v-flex>
      <v-btn
        small
        class="btnClose"
        icon
        @click.prevent="hideInfos"
        :title="$t('Pilot.MissionInfos.HideFleetInfosPanelTooltip')"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-layout>
    <multipane
      :class="{ 'mt-2': !(fleetState && fleetState.infosPanelVisible) }"
      :style="{ flexGrow: 1 }"
      class="custom-resizer mb-2"
      @paneResizeStop="panResized"
      layout="vertical"
    >
      <fleet-detail
        :isleft="true"
        class="fleet-infos border-box elevation-2 ml-2"
        :class="{ transition: isTransitionActive }"
        :style="{ width: currentDetailFleet.leftPanelWidth, flex: '0 0 auto' }"
      />
      <multipane-resizer class="pa-1">
        <v-btn
          @click="resizePaneLeft('0px')"
          icon
          class="toggle-left"
          :title="$t('Pilot.FleetInfos.ExpandRightPanelTooltip')"
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <br />
        <v-btn
          @click="resizePaneLeft('50%')"
          icon
          class="toggle-left"
          :title="$t('Pilot.FleetInfos.EqualRightLeftPanelTooltip')"
        >
          ||
        </v-btn>
        <br />
        <v-btn
          @click="resizePaneLeft('calc(100% - 22px)')"
          icon
          class="toggle-right"
          :title="$t('Pilot.FleetInfos.ExpandLeftPanelTooltip')"
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </multipane-resizer>
      <fleet-detail
        :isleft="false"
        class="fleet-infos border-box elevation-2 mr-2"
        style="flex: 1 1 auto; overflow: auto"
      />
    </multipane>
  </v-flex>
</template>
<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'

import FleetDetail from '@/components/fleet/FleetDetail.vue'
import { AloMission, GLIDER_MODE } from '@/models/alo/AloMission'
import Multipane from '@/components/shared/multipane/multipane.vue'
import MultipaneResizer from '@/components/shared/multipane/multipane-resizer.vue'
import distance from '@turf/distance'
import { point } from '@turf/helpers'
import { buildFleetUIStateId, FleetUiState } from '@/models/ui/FleetUiState'
import { DETAIL_REFUSED } from '@/utils/tenantModules'

@Component({
  name: 'FleetPage',
  props: {
    fleetid: Number,
  },
  components: { FleetDetail, Multipane, MultipaneResizer },
})
export default class FleetPage extends Vue {
  fleetid!: number
  isTransitionActive = false

  get currentDetailFleet() {
    return this.$store.myGetters.currentDetailFleet
  }

  get fleet() {
    return this.currentDetailFleet && this.currentDetailFleet.fleet
  }

  get waitingMode() {
    const waitingModes = this.missions.map(m => m.gliderWaitingMode)
    if (waitingModes.indexOf(GLIDER_MODE.GLIDER_MODE_DISCONNECTED) > -1) return GLIDER_MODE.GLIDER_MODE_DISCONNECTED
    if (waitingModes.indexOf(GLIDER_MODE.GLIDER_MODE_WAITING_FOR_RESUME) > -1)
      return GLIDER_MODE.GLIDER_MODE_WAITING_FOR_RESUME
    if (waitingModes.indexOf(GLIDER_MODE.GLIDER_MODE_WAITING_FOR_GO) > -1) return GLIDER_MODE.GLIDER_MODE_WAITING_FOR_GO
    if (waitingModes.indexOf(GLIDER_MODE.GLIDER_MODE_DISCONNECTED) > -1) return GLIDER_MODE.GLIDER_MODE_DISCONNECTED
    return null
  }

  get missions() {
    return this.fleet.param?.missionIds?.map(id => this.$store.myGetters.missionsById[id])?.filter(m => !!m) ?? []
  }

  @Watch('missions')
  onMissionChanged() {
    this.ensureMissionFleetYos()
  }

  ensureMissionFleetYos() {
    const queryIds = this.missions
      ?.map(m => m.gliId!)
      ?.filter(id => this.$store.myGetters.missionsPassedLoaded.every(pid => pid != id))
    if (queryIds?.length) {
      this.$store.myActions.MISSION_REQUEST({ ongoing: false, yos: true, gliIds: queryIds })
    }
  }

  get allYosDates() {
    return this.missions.flatMap(m => m.yoArray.map(y => y.yoGpsDatTime))
  }

  get startDate() {
    if (this.allYosDates.length > 0)
      return this.allYosDates.reduce((a, b) => {
        return a < b ? a : b
      })
  }

  get lastDate() {
    if (this.allYosDates.length > 0)
      return this.allYosDates.reduce((a, b) => {
        return a > b ? a : b
      })
  }

  get coordFormatFilter() {
    return this.$store.myGetters.coordFormatFilter
  }

  get isDrawerOpen() {
    return this.$store.myGetters.isDrawerOpen
  }

  get isRightDrawerOpen() {
    return this.$store.myGetters.isRightDrawerOpen
  }

  get showDrawer() {
    return this.isDrawerOpen
  }
  set showDrawer(newValue) {
    this.$store.myActions.UI_SET_DRAWER_VISIBILITY(newValue)
  }

  get showRightDrawer() {
    return this.isRightDrawerOpen
  }
  set showRightDrawer(newValue) {
    this.$store.myActions.UI_SET_RIGHT_DRAWER_VISIBILITY(newValue)
  }

  get clipped() {
    return this.$vuetify.breakpoint.name == 'xs' || this.$vuetify.breakpoint.name == 'sm'
  }

  get fleet_uiid() {
    return buildFleetUIStateId(this.currentDetailFleet.fleet.FLE_ID!, 'detail')
  }

  //recupere le state de travail
  get fleetState() {
    //retourne un MapUiState
    return this.$store.myGetters.fleetUiStateByUiid(this.fleet_uiid)
  }

  @Watch('$route')
  onRouteChanged(to: string, from: string) {
    this.openFleet()
  }

  created() {
    this.openFleet()
  }

  // BLF037 — flotte refusée par les réglages de son enseigne : « introuvable », retour à la liste.
  openFleet() {
    this.$store.myActions.FLEET_SELECT_DETAIL_REQUEST(this.fleetid).catch((err: unknown) => {
      if ((err as Error)?.message !== DETAIL_REFUSED) throw err
      this.$store.myActions.UI_ADD_SNACK_MESSAGE(this.$t('Fleet.NotFound') as string)
      this.$router.replace('/fleet')
    })
  }

  resizePaneLeft(size: string) {
    //force la transition
    this.isTransitionActive = true
    this.$store.myActions.FLEET_SET_LEFTPANEL_SIZE(size)
    //desactive la transition
    setTimeout(() => {
      this.isTransitionActive = false
    }, 250)
  }

  panResized(pane: any, resizer: any, size: string) {
    this.$store.myActions.FLEET_SET_LEFTPANEL_SIZE(size)
  }

  toggleRightDrawer() {
    if (this.clipped) this.showDrawer = false
    this.showRightDrawer = !this.showRightDrawer
  }

  hideInfos() {
    if (this.fleetState.infosPanelVisible)
      this.$store.myActions.UI_TOGGLE_MISSION_INFO_PANEL_VISIBILITY(this.fleet_uiid)
  }
}
</script>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.btnClose {
  position: sticky;
  top: 0px;
  right: 0px;
  background: #ddd;
}
.data {
  color: var(--primary-color);
  padding: 0 10px;
}
.toggle-left,
.toggle-right {
  left: 0px;
  margin: 0px;
  padding: 0px;
  max-width: 100%;
  position: relative;
}

.transition {
  transition: all 250ms ease;
}

.fleet-page {
  background-color: #ddd;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.fleet-infos {
  background-color: #f4f4f4;
}

.fleet-resume {
  white-space: nowrap;
  overflow-x: auto;
}
</style>

<style>
.custom-resizer > .multipane-resizer {
  flex-grow: 1;
  height: auto;
  margin: 0;
  left: 0;
  min-width: 15px;
  max-width: 15px;
  position: relative;
  &:before {
    display: block;
    content: '';
    width: 3px;
    height: 40px;
    position: absolute;
    top: 50%;
    left: 50%;
    margin-top: -20px;
    margin-left: -1.5px;
    border-left: 1px solid #ccc;
    border-right: 1px solid #ccc;
  }
  &:hover {
    &:before {
      border-color: #999;
    }
  }
}
</style>
